import React, { useState } from 'react';
import { Modal, Button, Spinner, Alert, Row, Col, Card, Form } from 'react-bootstrap';
import { FaExclamationTriangle, FaSync } from 'react-icons/fa';

/**
 * Component for displaying raw webhook message details in a modal
 */
const RawMessageModal = ({ 
    show, 
    onHide, 
    rawMessage, 
    rawMessageLoading, 
    rawMessageError,
    formatISOToLocalDateTime
}) => {
    return (
        <Modal 
            show={show} 
            onHide={onHide}
            size="xl"
            dialogClassName="modal-wide"
            fullscreen="lg-down"
        >
            <Modal.Header closeButton>
                <Modal.Title>Raw Webhook Message</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {rawMessageLoading ? (
                    <div className="text-center py-4">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-3">Loading raw message...</p>
                    </div>
                ) : rawMessageError ? (
                    <Alert variant="danger" className="mb-0">
                        <FaExclamationTriangle className="me-2" />
                        {rawMessageError}
                    </Alert>
                ) : (
                    <Row>
                        <Col md={7}>
                            <div>
                                <div className="d-flex gap-2 mb-3">
                                    <Button 
                                        variant="outline-secondary"
                                        onClick={() => {
                                            // Copy the displayed JSON content to clipboard
                                            const pre = document.getElementById('jsonContent');
                                            if (pre) {
                                                const text = pre.innerText;
                                                navigator.clipboard.writeText(text);
                                                alert('JSON copied to clipboard!');
                                            } else {
                                                alert('Could not find JSON content to copy.');
                                            }
                                        }}
                                    >
                                        Copy JSON
                                    </Button>
                                    
                                    <ReplayMessageButton rawMessage={rawMessage} />
                                </div>
                                <div 
                                    style={{ 
                                        maxHeight: '70vh', 
                                        overflow: 'auto',
                                        backgroundColor: '#f8f9fa',
                                        padding: '15px',
                                        borderRadius: '5px'
                                    }}
                                >
                                    <pre id="jsonContent" style={{ whiteSpace: 'pre-wrap' }}>
                                        {typeof rawMessage === 'string' 
                                            ? rawMessage 
                                            : typeof rawMessage?.message === 'string'
                                                ? rawMessage.message
                                                : JSON.stringify(rawMessage?.message || rawMessage, null, 2)}
                                    </pre>
                                </div>
                            </div>
                        </Col>
                        <Col md={5}>
                            <Card className="h-100">
                                <Card.Header className="bg-primary text-white">
                                    <h5 className="mb-0">Key Information</h5>
                                </Card.Header>
                                <Card.Body style={{ overflow: 'auto', maxHeight: '65vh' }}>
                                    {typeof rawMessage?.message === 'object' && rawMessage?.message !== null ? (
                                        <div>
                                            <Form.Group className="mb-3">
                                                <Form.Label className="fw-bold">Event Type</Form.Label>
                                                <Form.Control 
                                                    plaintext 
                                                    readOnly 
                                                    value={rawMessage.message?.event || 'N/A'} 
                                                    className="bg-light p-2"
                                                />
                                            </Form.Group>
                                            
                                            <Form.Group className="mb-3">
                                                <Form.Label className="fw-bold">Reservation ID</Form.Label>
                                                <Form.Control 
                                                    plaintext 
                                                    readOnly 
                                                    value={rawMessage.message?.reservation?.id || rawMessage.message?.reservation?._id || 'N/A'} 
                                                    className="bg-light p-2"
                                                />
                                            </Form.Group>
                                            
                                            <Form.Group className="mb-3">
                                                <Form.Label className="fw-bold">Reservation Checkout</Form.Label>
                                                <Form.Control 
                                                    plaintext 
                                                    readOnly 
                                                    value={formatISOToLocalDateTime(rawMessage.message?.reservation?.checkOut) || 'N/A'} 
                                                    className="bg-light p-2"
                                                />
                                            </Form.Group>
                                            
                                            <Form.Group className="mb-3">
                                                <Form.Label className="fw-bold">Reservation Status</Form.Label>
                                                <Form.Control 
                                                    plaintext 
                                                    readOnly 
                                                    value={rawMessage.message?.reservation?.status || 'N/A'} 
                                                    className="bg-light p-2"
                                                />
                                            </Form.Group>
                                            
                                            <Form.Group className="mb-3">
                                                <Form.Label className="fw-bold">Listing Full Address</Form.Label>
                                                <Form.Control 
                                                    plaintext 
                                                    readOnly 
                                                    value={rawMessage.message?.reservation?.listing?.address?.full || 'N/A'} 
                                                    className="bg-light p-2"
                                                />
                                            </Form.Group>
                                            
                                            <Form.Group className="mb-3">
                                                <Form.Label className="fw-bold">Reservation Before Checkout</Form.Label>
                                                <Form.Control 
                                                    plaintext 
                                                    readOnly 
                                                    value={formatISOToLocalDateTime(rawMessage.message?.reservationBefore?.checkOut) || 'N/A'} 
                                                    className="bg-light p-2"
                                                />
                                            </Form.Group>
                                            
                                            <Form.Group className="mb-3">
                                                <Form.Label className="fw-bold">Reservation Before Status</Form.Label>
                                                <Form.Control 
                                                    plaintext 
                                                    readOnly 
                                                    value={rawMessage.message?.reservationBefore?.status || 'N/A'} 
                                                    className="bg-light p-2"
                                                />
                                            </Form.Group>
                                            
                                            <Form.Group className="mb-3">
                                                <Form.Label className="fw-bold">Listing Tags</Form.Label>
                                                <div className="bg-light p-2 rounded">
                                                    {rawMessage.message?.reservation?.listing?.tags && rawMessage.message.reservation.listing.tags.length > 0 ? (
                                                        <div className="d-flex flex-wrap gap-1">
                                                            {rawMessage.message.reservation.listing.tags.map((tag, index) => (
                                                                <span 
                                                                    key={index}
                                                                    className="badge bg-primary"
                                                                    style={{ fontSize: '0.75rem' }}
                                                                >
                                                                    {tag}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <span className="text-muted">No tags available</span>
                                                    )}
                                                </div>
                                            </Form.Group>
                                        </div>
                                    ) : (
                                        <Alert variant="warning">
                                            Cannot extract information from non-JSON message
                                        </Alert>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    Close
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

/**
 * Button component for replaying a webhook message with a new GUID
 */
const ReplayMessageButton = ({ rawMessage }) => {
    const [isReplaying, setIsReplaying] = useState(false);
    const [replayStatus, setReplayStatus] = useState(null);
    
    // Function to generate a new UUID v4
    const generateUUID = () => {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
            const r = Math.random() * 16 | 0;
            const v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    };
    
    const handleReplayMessage = async () => {
        if (!rawMessage || typeof rawMessage !== 'object') {
            setReplayStatus({
                success: false,
                message: 'Cannot replay: Invalid message format'
            });
            return;
        }
        
        try {
            setIsReplaying(true);
            setReplayStatus(null);
            
            // Extract cosCustomerId directly from the rawMessage object
            const cosCustomerId = rawMessage.cosCustomerId;
            
            if (!cosCustomerId) {
                throw new Error('Customer ID not found in message');
            }
            
            // Determine the actual message content to replay
            let messageContent;
            
            // Get the message content from rawMessage.message
            const messageData = rawMessage.message;
            
            // Check if the message has the expected structure with event and meta
            if (typeof messageData === 'object' && messageData !== null) {
                if (messageData.event && messageData.meta) {
                    // The webhook message is directly in messageData
                    messageContent = messageData;
                } else {
                    throw new Error('Message does not have expected event and meta structure');
                }
            } else if (typeof messageData === 'string') {
                // Try to parse the message content from a string
                try {
                    const parsed = JSON.parse(messageData);
                    if (parsed.event && parsed.meta) {
                        messageContent = parsed;
                    } else {
                        throw new Error('Parsed message does not have expected event and meta structure');
                    }
                } catch (e) {
                    console.error('Error parsing message content:', e);
                    throw new Error('Could not parse message content as JSON');
                }
            } else {
                throw new Error('Could not determine message content structure');
            }
            
            // Create a deep copy of the message content
            const messageToReplay = JSON.parse(JSON.stringify(messageContent));
            
            // Replace the UUIDs in the meta section
            if (messageToReplay.meta) {
                // Generate new UUIDs for eventId and messageId
                if (messageToReplay.meta.eventId) {
                    messageToReplay.meta.eventId = generateUUID();
                }
                
                if (messageToReplay.meta.messageId) {
                    messageToReplay.meta.messageId = generateUUID();
                }
                
                console.log('Generated new UUIDs:', messageToReplay.meta);
            } else {
                throw new Error('Message does not have meta section for UUID replacement');
            }
            
            // Send the POST request
            const response = await fetch(`/goapi/webhook/guesty/${cosCustomerId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(messageToReplay),
            });
            
            if (response.ok) {
                setReplayStatus({
                    success: true,
                    message: `Message successfully replayed with status ${response.status}`
                });
            } else {
                const errorText = await response.text();
                throw new Error(`Failed to replay message: ${response.status} ${errorText}`);
            }
        } catch (error) {
            console.error('Error replaying message:', error);
            setReplayStatus({
                success: false,
                message: error.message || 'Failed to replay message'
            });
        } finally {
            setIsReplaying(false);
        }
    };
    
    return (
        <>
            <Button 
                variant="outline-primary"
                onClick={handleReplayMessage}
                disabled={isReplaying}
            >
                {isReplaying ? (
                    <>
                        <Spinner 
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="me-1"
                        />
                        Replaying...
                    </>
                ) : (
                    <>
                        <FaSync className="me-1" /> Replay Message
                    </>
                )}
            </Button>
            
            {replayStatus && (
                <Alert 
                    variant={replayStatus.success ? "success" : "danger"}
                    className="mt-2"
                >
                    {replayStatus.message}
                </Alert>
            )}
        </>
    );
};

export default RawMessageModal;
